import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, FormControl, UntypedFormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { from, merge } from 'rxjs';
import { debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';

import { PreferenceService } from '../../core';
import { GetTrainingListParams, TrainingListItemModel } from '../models/training.models';
import { TrainingRepositoryService } from '../training.repository';
import { ConfirmationService } from '../../shared';

@Component({
   selector: 'am-training-list',
   templateUrl: 'training-list.component.html',
   styleUrls: ['training-list.component.scss']
})
export class TrainingListComponent implements OnInit {
   @ViewChild(MatSort, { static: true }) sort: MatSort;
   @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

   dataSource = new MatTableDataSource<TrainingListItemModel>([]);
   loading = true;
   columnsToDisplay = ['date', 'title', 'status', 'actions'];
   returnedItemsCount = 0;
   cases: TrainingListItemModel[];
   filterForm: UntypedFormGroup;
   usersFilter = [];
   statusFilter = [];
   dateFilter = {};
   showFilters = false;

   constructor(
      private formBuilder: UntypedFormBuilder,
      private trainingService: TrainingRepositoryService,
      private preferenceService: PreferenceService,
      private confirmation: ConfirmationService) {
   }

   ngOnInit() {
      this.initialize();
      this.subscribe();
      this.getTrainingList();
   }

   toggleFilters() {
      this.showFilters = !this.showFilters;
   }

   getParameters() {
      const order = this.sort.active || 'startTime';
      this.dateFilter = this.filterForm.get('dateFilter').value;
      this.statusFilter = this.filterForm.get('statusFilter').value;
      this.usersFilter = this.filterForm.get('usersFilter').value;
      this.paginator.pageSize = this.paginator.pageSize || 25;
      const titleFilter = this.filterForm.get('titleFilter').value;

      this.savePreferences();

      const params: GetTrainingListParams = {
         order: this.sort.direction === 'desc' ? '-' + order : order,
         page: this.paginator.pageIndex + 1,
         pageSize: this.paginator.pageSize,
         ...this.dateFilter,
         status: this.statusFilter,
         userIds: this.usersFilter,
         title: titleFilter
      };

      return params;
   }

   private getTrainingList() {
      this.dataSource.data = [];

      this.loading = true;
      const params = this.getParameters();

      return this.trainingService.getTrainingList(params)
         .subscribe(data => {
            this.loading = false;
            this.returnedItemsCount = data.count;
            this.dataSource.data = data.list;
         });
   }

   export = () => {
      const params = this.getParameters();
      this.trainingService.getTrainingListDownload(params)
         .subscribe();
   }

   private subscribe() {
      this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
      merge(this.sort.sortChange, this.paginator.page, this.filterForm.valueChanges)
      .pipe(
         debounceTime(500)
      )
      .subscribe(() => this.getTrainingList());
   }

   private initialize() {
      this.loadPreferences();

      this.filterForm = this.formBuilder.group({
         dateFilter: this.dateFilter,
         statusFilter: [this.statusFilter],
         usersFilter: [this.usersFilter],
         titleFilter: null
      });
   }

   private loadPreferences(): any {
      const preferences: any = this.preferenceService.getPreference('trainingList');
      if (preferences) {
         Object.assign(this.sort, preferences.sortSettings);
         Object.assign(this.paginator, preferences.paginatorSettings);
         this.statusFilter = preferences.statusFilter;
         this.usersFilter = preferences.usersFilter;
         Object.assign(this.dateFilter, preferences.dateFilter);
      }
   }

   private savePreferences() {
      const preferences = {
         sortSettings: { active: this.sort.active, direction: this.sort.direction },
         paginatorSettings: { pageSize: this.paginator.pageSize },
         dateFilter: this.dateFilter,
         statusFilter: this.statusFilter,
         usersFilter: this.usersFilter
      };
      this.preferenceService.setPreference('trainingList', preferences);
   }

   showDeleteDialog(training: TrainingListItemModel) {
      this.confirmation.confirm('Delete Training?', 'DELETE', 'CANCEL').pipe(
         filter(ok => ok),
         switchMap(() => this.trainingService.deleteTraining(training.id))
      ).subscribe(() => {
         const index = this.dataSource.data.indexOf(training);
         this.dataSource.data = [
            ...this.dataSource.data.slice(0, Math.max(index, 0)),
            ...this.dataSource.data.slice(index + 1)
         ];
      });
   }

   clearFilters() {
      this.filterForm.patchValue({
         dateFilter: {},
         legalTeamFilter: [],
         statusFilter: null,
         usersFilter: [],
         titleFilter: null
      });
   }
}



















